/**
 * T-24 · La red de seguridad del fragmento que ya no existe.
 *
 * El panel se parte en fragmentos con huella en el nombre
 * (`Pantalla-TDe3fOfd.js`). Al publicar una versión nueva, esos nombres cambian.
 * Quien tuviera el panel abierto en otra ventana sigue con el `index.html`
 * viejo en memoria, que apunta a nombres que **acaban de dejar de existir**: en
 * cuanto navegue a un módulo, el `import()` falla y la pantalla se queda en
 * blanco. Sin mensaje, porque un error de red en un `import()` no lo atrapa
 * ningún `ErrorBoundary` colocado como se coloca normalmente.
 *
 * `T-10` conserva la versión anterior en el servidor precisamente para que esta
 * ventana sea estrecha. Esto es lo que cubre el resto: **recargar una vez**, que
 * trae el `index.html` nuevo —servido sin caché— y con él los nombres buenos.
 *
 * > [!danger] Una vez, y con marca
 * > Recargar sin marca es un bucle infinito de recargas si el fallo no era del
 * > fragmento sino de la red, del servidor o de un bloqueador. Una pantalla en
 * > blanco es mala; una pestaña que se recarga sola para siempre, con la batería
 * > del teléfono de una dueña de cafetería, es peor.
 *
 * La marca vive en `sessionStorage` —por pestaña, y muere al cerrarla— y **lleva
 * hora**: pasada la ventana, una caída de red meses después vuelve a tener
 * derecho a su recarga.
 */

/** Cuánto tiene que pasar para que una segunda recarga vuelva a estar permitida. */
export const VENTANA_MS = 30_000

const CLAVE = 'fialrest:recarga-por-fragmento'

export interface Entorno {
  readonly ahora: () => number
  readonly leer: (clave: string) => string | null
  readonly escribir: (clave: string, valor: string) => void
  readonly recargar: () => void
}

/**
 * `sessionStorage` lanza en algunos contextos —modo privado de ciertos
 * navegadores, ajustes que bloquean el almacenamiento—, y ahí el panel tiene que
 * seguir funcionando. Sin marca legible se recarga igual: es preferible una
 * recarga de más a una pantalla en blanco.
 */
export const entornoDelNavegador: Entorno = {
  ahora: () => Date.now(),
  leer: (c) => { try { return sessionStorage.getItem(c) } catch { return null } },
  escribir: (c, v) => { try { sessionStorage.setItem(c, v) } catch { /* sin marca */ } },
  recargar: () => { window.location.reload() },
}

/** ¿Toca recargar, o ya se intentó hace nada? */
export function debeRecargar(entorno: Entorno): boolean {
  const marca = Number(entorno.leer(CLAVE))
  if (!Number.isFinite(marca) || marca === 0) return true
  return entorno.ahora() - marca > VENTANA_MS
}

/**
 * Envuelve la carga de un fragmento. Si falla y toca, recarga; si ya se recargó,
 * deja subir el error para que lo vea quien lo pueda enseñar.
 */
export function conRecargaUnica<T>(
  cargar: () => Promise<T>, entorno: Entorno = entornoDelNavegador,
): () => Promise<T> {
  return async () => {
    try {
      return await cargar()
    } catch (error) {
      if (!debeRecargar(entorno)) throw error

      entorno.escribir(CLAVE, String(entorno.ahora()))
      entorno.recargar()

      // La página se está yendo. Devolver algo —o lanzar— haría que React
      // pintara un instante de pantalla rota antes de irse; esta promesa no
      // resuelve nunca y deja el `Suspense` en su estado de carga hasta el final.
      return new Promise<T>(() => {})
    }
  }
}
